/**
 * Google Analytics 4 Integration
 * 
 * Tracks key events: contact_submit, book_call_click, pricing_cta_click
 */

import ReactGA from 'react-ga4';

const GA_MEASUREMENT_ID = process.env.REACT_APP_GA4_MEASUREMENT_ID;

// Initialize GA4
export const initializeGA4 = () => {
  if (!GA_MEASUREMENT_ID) {
    console.warn('GA4 Measurement ID not configured');
    return;
  }
  
  ReactGA.initialize(GA_MEASUREMENT_ID, {
    gaOptions: {
      anonymize_ip: true,
    },
  });
  
  console.log('GA4 initialized:', GA_MEASUREMENT_ID);
};

// Track page view
export const trackPageView = (path, title) => {
  if (!GA_MEASUREMENT_ID) return;
  
  ReactGA.send({
    hitType: 'pageview',
    page: path,
    title: title || document.title,
  });
};

// Track custom event
export const trackEvent = (eventName, params = {}) => {
  if (!GA_MEASUREMENT_ID) return;
  
  ReactGA.event(eventName, {
    ...params,
    timestamp: new Date().toISOString(),
  });
};

// Track contact form submission
export const trackContactSubmit = (formType = 'contact', source = 'direct') => {
  trackEvent('contact_submit', {
    form_type: formType,
    source: source,
    page_location: window.location.pathname,
  });
};

// Track book a call click
export const trackBookCallClick = (buttonLocation = 'hero') => {
  trackEvent('book_call_click', {
    button_location: buttonLocation,
    page_location: window.location.pathname,
  });
};

// Track pricing CTA click
export const trackPricingCtaClick = (pricingTier, ctaText = 'Get Started') => {
  trackEvent('pricing_cta_click', {
    pricing_tier: pricingTier,
    cta_text: ctaText,
    page_location: window.location.pathname,
  });
};

// Track conversion (booking completed, quote requested, etc.)
export const trackConversion = (conversionType, value = null) => {
  trackEvent('conversion', {
    conversion_type: conversionType,
    value: value,
    currency: 'GBP',
  });
};

// Get GA client ID for server-side tracking
export const getClientId = () => {
  return new Promise((resolve) => {
    if (!GA_MEASUREMENT_ID) {
      resolve(crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substring(2));
      return;
    }
    
    // Try to get from GA cookies
    const gaCookie = document.cookie
      .split('; ')
      .find(row => row.startsWith('_ga='));
    
    if (gaCookie) {
      const parts = gaCookie.split('.');
      if (parts.length >= 4) {
        resolve(`${parts[2]}.${parts[3]}`);
        return;
      }
    }
    
    // Fallback to random ID
    resolve(crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substring(2));
  });
};

export default {
  initializeGA4,
  trackPageView,
  trackEvent,
  trackContactSubmit,
  trackBookCallClick,
  trackPricingCtaClick,
  trackConversion,
  getClientId,
};
